import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, OnDestroy, OnInit, Type, inject } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import {
	DELAY_BEFORE_WS_RECONNECTION,
	ErrorStatuses,
	Languages,
	NavBarAction,
	Notification,
	NotificationModel,
	NotificationsPayloadTypes,
	NotificationTypeEnum,
	NotificationTypesModel,
	UniformFilesLink,
	WebSocketErrors,
	WebSocketStringTypes,
	WebSocketTypes
} from '@core';
import { ReportTimerService } from '@app/services/report-timer/report-timer.service';
import { BaseNewReport } from '@app/components/base-new-report.abstract';
import { NotificationSettingsService } from '@app/services/notification-settings/notification-settings.service';
import { WebSocketService } from '@app/services/web-socket/web-socket.service';
import { catchError, filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { Params, Router } from '@angular/router';
import { WebSocketSubject } from 'rxjs/webSocket';
import { FilterReportsDropdownService } from '@app/services/filter-reports-dropdown/filter-reports-dropdown.service';
import { Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { Timer } from '@app/modules/views/reports/timer.model';
import { ModalType } from '@ui/lib/shared-services';
import { NotificationActionsService } from '@app/services/notification-actions/notification-actions.service';
import { AuthenticationService } from '@app/core/services/authentication/authentication.service';
import { SideBarAction } from '@app/components/general/side-bar/side-bar.component';
import { MainRoutes } from '@app/shared/enums/general.enums';
import { OrganizationRoute } from '@app/pages/organization/enums/organization.enums';
import { ReportModalConfig } from '@app/components/report-form-config.type';
import { ReportTypesEnum } from '@app/core/services/reports/reports.enum';
import { User } from '@app/core/services/user/user.model';
import { Settings } from '@app/core/services/settings/settings.model';
import { CreationDrawerComponent } from '@app/shared/components/new-drawer/creation/creation-drawer/creation-drawer.component';
import { SearchDrawerComponent } from '@app/shared/components/new-drawer/search/search-drawer/search-drawer.component';
import { DrawerService } from '@app/shared/services/drawer/drawer.service';
import {
	CreationDrawerInitialData,
	SearchDrawerInitialData
} from '@app/shared/components/new-drawer/models/new-drawer.model';
import {
	ItemOptionsValue,
	SearchOptionsValue,
	TopicOptionsValue
} from '@app/shared/components/new-drawer/new-drawer.enums';
import { AccountingPeriodForm } from '@ui/lib/shared-modules/modals/bookkeeping-period/bookkeeping-period.component';
import { Translation } from '@ngx-translate/core';

@Component({
	selector: 'lawyal-home-component',
	templateUrl: './home.component.html',
	styleUrls: ['./home.component.scss'],
	standalone: false
})
export class HomeComponent extends BaseNewReport implements OnInit, OnDestroy {
	private webSocketService = inject(WebSocketService);
	private notificationSettingsService = inject(NotificationSettingsService);
	private authenticationService = inject(AuthenticationService);
	private timerService = inject(ReportTimerService);
	private router = inject(Router);
	private dropdownService = inject(FilterReportsDropdownService);
	private notificationActionService = inject(NotificationActionsService);
	private drawerService = inject(DrawerService);

	private readonly SEARCH_DRAWER_ROUTE_TO_ITEM_TYPE_MAP: Record<string, SearchOptionsValue> = {
		[`${MainRoutes.Organization}/${OrganizationRoute.Contacts}`]: SearchOptionsValue.Contacts,
		[`${MainRoutes.Organization}/${OrganizationRoute.Employees}`]: SearchOptionsValue.Employees,
		[`${MainRoutes.Organization}/${OrganizationRoute.Recipients}`]: SearchOptionsValue.Recipients
	};
	private readonly CREATION_DRAWER_ROUTE_TO_ITEM_TYPE_MAP: Record<string, ItemOptionsValue> = {
		[`${MainRoutes.Organization}/${OrganizationRoute.Contacts}`]: ItemOptionsValue.Organization,
		[`${MainRoutes.Organization}/${OrganizationRoute.Employees}`]: ItemOptionsValue.Organization,
		[`${MainRoutes.Organization}/${OrganizationRoute.Companies}`]: ItemOptionsValue.Organization
	};
	private readonly CREATION_DRAWER_ROUTE_TO_TOPIC_TYPE_MAP: Record<string, TopicOptionsValue> = {
		[`${MainRoutes.Organization}/${OrganizationRoute.Contacts}`]: TopicOptionsValue.Contact,
		[`${MainRoutes.Organization}/${OrganizationRoute.Employees}`]: TopicOptionsValue.Employee,
		[`${MainRoutes.Organization}/${OrganizationRoute.Companies}`]: TopicOptionsValue.Company
	};

	private webSocketTriggers: { [key: string]: Subject<any> }[] = [
		{ [WebSocketStringTypes[WebSocketTypes.NOTIFICATIONS]]: new Subject<any>() }
	];
	private uniformFilesLink: UniformFilesLink = { bkmvLink: '', iniLink: '' };
	private errorCounter: number;
	private activeSideBarAction: SideBarAction;
	private drawerCloseSubscription: Subscription;

	public notifications: Notification[] = [];
	public notificationAdded: Notification;
	public notificationDeleted: Notification;
	public userData$: Observable<User>;
	public phoneBreakPoint$: Observable<BreakpointState>;
	public timers: Timer[];
	public settingsReadyState$: Observable<boolean>;
	public settings: Settings;
	public sideBarAction: SideBarAction;
	public isLoading: boolean;
	public activeComponent: Type<any> | null = null;

	constructor() {
		super();
		this.phoneBreakPoint$ = this.breakpointObserver.observe(Breakpoints.XSmall);
	}

	ngOnInit() {
		this.initApp();
		this.registerStoreChanges();
	}

	private initApp(): void {
		this.settingsService.get();
		this.getSettings();
		this.getTimers();
		this.registerMigrationModalChanges();
		this.dropdownService.initialDataForDropDown();
		this.checkMobileVersionModal();
	}

	private checkMobileVersionModal(): void {
		const currentUser: User = this.userService.getActiveUser();

		if (!currentUser || this.breakpointObserver.isMatched(Breakpoints.XSmall)) {
			return;
		}

		this.apiService
			.getUserJsonData(currentUser.id)
			.pipe(
				filter(data => data?.json_data?.showMobileVersionModal !== false),
				switchMap(() => {
					const translation: Translation = this.translateService.instant('MODALS.NEW_MOBILE_VERSION');
					return this.modalService.openModal(ModalType.NewMobileVersion, { translation });
				}),
				switchMap(() => this.apiService.updateUserJsonData(currentUser.id, { showMobileVersionModal: false })),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
	}

	private registerMigrationModalChanges(): void {
		this.notificationActionService.notificationActionOpenMigrationModal$
			.pipe(
				filter(data => !!data.openMigrationModal),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(data => {
				this.openMigrationInAppModal(data.notification);
			});
	}

	private getTimers(): void {
		this.timerService.initialTimers();
		this.timerService.timersStreamer$
			.pipe(
				tap(timers => {
					this.timers = timers;
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
	}

	private registerStoreChanges(): void {
		this.userData$ = this.userService.activeUser$;
		this.isLoading = true;
		this.settingsReadyState$ = this.settingsService.settingsReadyState$.pipe(
			filter(stateIsReady => !!stateIsReady),
			tap(() => {
				this.settings = this.settingsService.settings;
				this.isLoading = false;
			}),
			takeUntilDestroyed(this.destroyRef)
		);

		this.getWebSocketNotifications();
	}

	private subscribeToDrawerClose(): void {
		if (!this.drawerCloseSubscription) {
			this.drawerCloseSubscription = this.drawerService.onClose$.subscribe(() => {
				this.sideBarAction = null;
			});
		}
	}

	//Nav Bar Actions:
	private openBookkeepingPeriodModal(): void {
		this.modalService
			.openModal(ModalType.BookkeepingPeriod)
			.pipe(
				filter(data => !!data),
				switchMap((res: AccountingPeriodForm) => {
					return this.apiService.updateFirmAccountingPeriod(this.mapBookkeepingPeriodToRequest(res));
				}),
				catchError(error => {
					this.toastService.error();
					throw error;
				}),
				tap(() => this.toastService.success())
			)
			.subscribe();
	}

	private mapBookkeepingPeriodToRequest(data: AccountingPeriodForm): Params {
		const month: Date = data.month ? new Date(data.month) : null;
		const year: Date = data.year ? new Date(data.year) : null;

		return {
			...(month && { accounting_period_month: (month.getMonth() + 1).toString() }),
			...(year && { accounting_period_year: year.getFullYear().toString() }),
			...((data.enabled === false || data.enabled) && { accounting_period_enabled: data.enabled })
		};
	}

	private openReportingPeriodModal(): void {
		this.modalService
			.openModal(ModalType.REPORTING_PERIOD)
			.pipe(
				filter(data => !!data),
				switchMap((params: Params) => {
					return this.apiService.updateFirmReportingPeriod(params);
				})
			)
			.subscribe(() => {
				this.toastService.success(this.translateService.instant('MODALS.REPORTING_PERIOD.REPORT_PERIOD_SUCCESS'));
			});
	}

	private openTaxApiSettingsModal(): void {
		this.modalService.openModal(ModalType.TaxApiSettings).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}

	private openSetVatValueModal(): void {
		const callbacks: Record<string, (params: Params) => Observable<any>> = {
			updateFirmVat: params => this.apiService.updateFirmVat(params),
			updateProformaInvoicesVat: params => this.apiService.updateProformaInvoicesVat(params)
		};
		const openToast: (translation: { [key: string]: any }) => void = (translation: { [key: string]: any }): void => {
			this.toastService.customNotification(translation.MESSAGE, translation.TITLE);
		};
		const initialData: Params = {
			proformaInvoiceVatUpdateStatuses: this.settingsService.getProformaInvoiceVatUpdateStatuses()
		};

		this.modalService
			.openModal(ModalType.UpdateVatModal, initialData, callbacks, null, openToast)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe();
	}

	private openDatesCalculatorModal(): void {
		this.modalService.openModal(ModalType.DatesCalculatorModal).pipe().subscribe();
	}

	private openWhatsAppSignupModal(): void {
		this.modalService.openModal(ModalType.WhatsAppSignup).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}

	private logout(): void {
		this.webSocketService.closeWebSockets();
		this.authenticationService.logout();
	}

	private getWebSocketNotifications = () => {
		this.settingsService.notificationsTypes$
			.pipe(
				filter(types => types && !!types.length),
				take(1),
				switchMap(() => this.getNotifications()),
				filter((res: any) => res && res.payload),
				takeUntil(
					this.webSocketTriggers[WebSocketTypes.NOTIFICATIONS][WebSocketStringTypes[WebSocketTypes.NOTIFICATIONS]]
				)
			)
			.subscribe(
				(res: NotificationModel) => {
					this.setNotifications(res);
					this.errorCounter = 0;
				},
				(error: ErrorEvent) => {
					this.webSocketErrorHandler(error.type as WebSocketErrors);
				},
				() => {
					if (this.authenticationService.token) {
						this.webSocketErrorHandler(WebSocketErrors.ERROR);
					}
				}
			);
	};

	private webSocketErrorHandler(errorType: WebSocketErrors): void {
		this.webSocketTriggers[WebSocketTypes.NOTIFICATIONS][WebSocketStringTypes[WebSocketTypes.NOTIFICATIONS]].complete();
		switch (errorType) {
			case WebSocketErrors.CLOSE:
				this.reconnectWebSocket(this.getWebSocketNotifications);
				break;

			//IF TOKEN IS BAD OR THIS IS NOT A CLOSE ERROR
			case WebSocketErrors.ERROR:
				this.errorCounter++;
				if (this.errorCounter > 60 / (DELAY_BEFORE_WS_RECONNECTION / 1000)) {
					this.authenticationService.logout();
				} else {
					setTimeout(() => {
						this.reconnectWebSocket(this.getWebSocketNotifications);
					}, DELAY_BEFORE_WS_RECONNECTION);
				}
				break;
		}
	}

	private reconnectWebSocket(reconnectFn: () => any): void {
		reconnectFn();
	}

	private getNotifications = (): WebSocketSubject<any> => this.notificationSettingsService.getNotificationsData();

	private setNotifications(res: NotificationModel): void {
		switch (res.type) {
			case NotificationsPayloadTypes.Notifications:
				this.notifications = res.payload as Notification[];
				break;

			case NotificationsPayloadTypes.Notification:
				this.checkNotificationType(res);
				this.notificationAdded = res.payload as Notification;
				this.passDataService.changeData(res);
				break;

			case NotificationsPayloadTypes.SetHidden:
				this.notificationDeleted = res.payload as Notification;
				break;
		}
	}

	private checkNotificationType(res: NotificationModel): void {
		const notification = res.payload as Notification;
		let notificationType: NotificationTypesModel = this.notificationActionService.getActualNotificationType(
			notification.type
		);
		if (notificationType?.type === NotificationTypeEnum.Bkmv || notificationType?.type === NotificationTypeEnum.Ini) {
			this.checkIfUniformFiles(res.payload as Notification);
		}
	}

	private checkIfUniformFiles(notification: Notification): void {
		let notificationType: NotificationTypesModel = this.notificationActionService.getActualNotificationType(
			notification.type
		);
		if (notificationType?.type === NotificationTypeEnum.Bkmv) {
			this.uniformFilesLink.bkmvLink = notification.message.link;
		} else if (notificationType?.type === NotificationTypeEnum.Ini) {
			this.uniformFilesLink.iniLink = notification.message.link;
		}

		if (this.uniformFilesLink.bkmvLink && this.uniformFilesLink.iniLink) {
			this.notificationSettingsService.setUniformFilesLink = { ...this.uniformFilesLink };
			this.uniformFilesLink.bkmvLink = '';
			this.uniformFilesLink.iniLink = '';
		} else {
			this.notificationSettingsService.setUniformFilesLink = null;
		}
	}

	private mapTimerToReport(timerData: Timer): Record<string, any> {
		return {
			...timerData,
			id: timerData.report?.id,
			time_report: {
				details: timerData.details,
				hours: timerData.active ? timerData.duration : timerData.hours
			}
		};
	}

	private updateTimerAfterError(): void {
		this.timerService.refreshTimersAfterError();
		const message: string = this.translateService.instant('NEW_NOTIFICATIONS.TIMER_DELETED.MESSAGE');
		const title: string = this.translateService.instant('NEW_NOTIFICATIONS.TIMER_DELETED.TITLE');
		this.toastService.customError(message, title);
	}

	public handleDrawerToggle(type: SideBarAction): void {
		let initialData: CreationDrawerInitialData | SearchDrawerInitialData;
		const urlWithoutParams: string = this.router.url.split('?')[0];
		this.sideBarAction = type;

		switch (type) {
			case SideBarAction.Create:
				this.activeComponent = CreationDrawerComponent;
				initialData =
					this.CREATION_DRAWER_ROUTE_TO_ITEM_TYPE_MAP[urlWithoutParams] &&
					this.CREATION_DRAWER_ROUTE_TO_TOPIC_TYPE_MAP[urlWithoutParams]
						? {
								item: this.CREATION_DRAWER_ROUTE_TO_ITEM_TYPE_MAP[urlWithoutParams],
								topic: this.CREATION_DRAWER_ROUTE_TO_TOPIC_TYPE_MAP[urlWithoutParams]
							}
						: null;
				break;
			case SideBarAction.Search: {
				this.activeComponent = SearchDrawerComponent;
				const searchTerms: string = new URLSearchParams(this.router.url.split('?')[1] ?? '').get('searchTerms');
				initialData = this.SEARCH_DRAWER_ROUTE_TO_ITEM_TYPE_MAP[urlWithoutParams]
					? { item: this.SEARCH_DRAWER_ROUTE_TO_ITEM_TYPE_MAP[urlWithoutParams], searchTerms }
					: null;
				break;
			}
		}

		if (this.drawerService.isOpen()) {
			if (this.sideBarAction === this.activeSideBarAction) {
				this.drawerService.close();
				this.activeSideBarAction = null;
				this.sideBarAction = null;
			} else {
				this.drawerService.close();
				this.drawerService.open(this.activeComponent, initialData);
			}
		} else {
			this.drawerService.open(this.activeComponent, initialData);
		}
		this.activeSideBarAction = type;
		this.subscribeToDrawerClose();
	}

	public handleNavBarAction(action: NavBarAction): void {
		switch (action) {
			case NavBarAction.CreateUniformFile:
				this.openCreateUniformFileModal();
				break;
			case NavBarAction.SystemSettings:
				break;
			case NavBarAction.NotificationsPanel:
				this.router.navigate([MainRoutes.NotificationsSettings]);
				break;
			case NavBarAction.Import:
				this.openMigrationInAppModal();
				break;
			case NavBarAction.BookkeepingPeriod:
				this.openBookkeepingPeriodModal();
				break;
			case NavBarAction.ReportingPeriod:
				this.openReportingPeriodModal();
				break;
			case NavBarAction.IsraelInvoicing:
				this.openTaxApiSettingsModal();
				break;
			case NavBarAction.SetVatValue:
				this.openSetVatValueModal();
				break;
			case NavBarAction.PersonalHelper:
				this.router.navigate([MainRoutes.PersonalSettings]);
				break;
			case NavBarAction.DatesCalculator:
				this.openDatesCalculatorModal();
				break;
			case NavBarAction.WhatsAppSignup:
				this.openWhatsAppSignupModal();
				break;
			case NavBarAction.Logout:
				this.logout();
				break;
		}
	}

	public changeLanguage(): void {
		const languageToUse: string = this.translateService.currentLang === Languages.HE ? Languages.EN : Languages.HE;
		this.translateService.use(languageToUse);
	}

	public startTimer(data: Timer): void {
		this.timerService
			.start(data)
			.pipe(
				catchError(err => {
					if (err.status === ErrorStatuses.NotFound) {
						this.updateTimerAfterError();
					}
					throw err;
				})
			)
			.subscribe(timers => {
				this.timerService.updateActiveTimer(timers);
				this.timerService.renderTimers(timers);
			});
	}

	public stopTimer(id: number): void {
		this.timerService
			.stop(id)
			.pipe(
				catchError(err => {
					if (err.status === ErrorStatuses.NotFound) {
						this.updateTimerAfterError();
					}
					throw err;
				})
			)
			.subscribe(timers => {
				this.timerService.renderTimers(timers);
			});
	}

	public openReportModalFromTimer(timerData: Timer): void {
		this.timerService
			.expand(timerData)
			.pipe(
				switchMap(timers => {
					let modalConfig: ReportModalConfig;
					const reportData: any = this.mapTimerToReport(timerData);
					this.timerService.renderTimers(timers);

					if (timerData.report?.id) {
						modalConfig = {
							duration_report: timerData.id,
							timer: timerData,
							reportData,
							isEditMode: true
						};
						return this.openEditOrViewReportModal(modalConfig);
					} else {
						modalConfig = {
							reportType: ReportTypesEnum.hours,
							reportData,
							timer: timerData,
							duration_report: timerData.id
						};
						return this.openNewReportModal(modalConfig);
					}
				}),
				catchError(err => {
					if (err.status === ErrorStatuses.NotFound) {
						this.updateTimerAfterError();
					}
					throw err;
				})
			)
			.subscribe(() => {
				this.passDataService.changeData({ [this.reportsStateService.storeValue.activeDisplay]: true });
				this.toastService.success(this.translateService.instant('TOAST.REPORT_SUCCESS'));
			});
	}

	ngOnDestroy() {
		this.webSocketTriggers.forEach((trigger, index) => {
			trigger[WebSocketStringTypes[index]].complete();
		});
	}
}
